import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

const SESSION_STORAGE_KEY = 'tilaajakanava_selected_org'
const URL_PARAM_KEY = 'org'

// UUID validation helper
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

interface OrganizationContextType {
  selectedOrgId: string
  setSelectedOrgId: (id: string) => void
  getShareableUrl: () => string
  clearOrgSelection: () => void
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedOrgId, setSelectedOrgIdState] = useState<string>('')
  const [initialized, setInitialized] = useState(false)

  // Initialize from URL param or sessionStorage on mount
  useEffect(() => {
    if (initialized) return

    // Priority 1: URL parameter
    const urlOrgId = searchParams.get(URL_PARAM_KEY)
    if (urlOrgId && isValidUUID(urlOrgId)) {
      setSelectedOrgIdState(urlOrgId)
      sessionStorage.setItem(SESSION_STORAGE_KEY, urlOrgId)
      // Remove org param from URL after reading (clean URL)
      const newParams = new URLSearchParams(searchParams)
      newParams.delete(URL_PARAM_KEY)
      setSearchParams(newParams, { replace: true })
      setInitialized(true)
      return
    }

    // Priority 2: sessionStorage
    const storedOrgId = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (storedOrgId && isValidUUID(storedOrgId)) {
      setSelectedOrgIdState(storedOrgId)
    }

    setInitialized(true)
  }, [searchParams, setSearchParams, initialized])

  // Wrapped setter that also updates sessionStorage
  const setSelectedOrgId = useCallback((id: string) => {
    setSelectedOrgIdState(id)
    if (id) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, id)
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    }
  }, [])

  // Generate shareable URL with org parameter
  const getShareableUrl = useCallback(() => {
    const url = new URL(window.location.href)
    if (selectedOrgId) {
      url.searchParams.set(URL_PARAM_KEY, selectedOrgId)
    }
    return url.toString()
  }, [selectedOrgId])

  // Clear organization selection
  const clearOrgSelection = useCallback(() => {
    setSelectedOrgIdState('')
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  }, [])

  return (
    <OrganizationContext.Provider value={{ selectedOrgId, setSelectedOrgId, getShareableUrl, clearOrgSelection }}>
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}
